/*
 * Copyright (c) 2023-2025. Haodong JU
 */

import { FireBaseAuth, FireBaseStore } from '@/model/firebase/google-fire-base'
import { docContentOf, resultOf } from '@/model/firebase/queryUtils'
import { collection, doc, query, setDoc } from 'firebase/firestore'
import router from '@/model/global/router'
import { enterStore, useStore } from '@/model/dataLayer/api/SaaS/storeService'
import { lastRoute, resetLastRoute } from '@/main'
import { getAllStoreIdForUser, getUserAndStoreRelationByDeviceId } from '@/model/dataLayer/api/SaaS/subscriptionService'
import GlobalConfig, {
  getOfflineUrl,
  markAppInitialized,
  Remember,
  requireOfflineMode
} from '@/model/global/GlobalSettings'
import { getCurrentDeviceId } from '@/model/dataLayer/service/RestaurantInfoService'
import { getActiveCodes } from '@/model/firebase/activeUtils'
import { sleep } from '@/model/dataLayer/api/SaaS/SaaSUtils'
import hillo from 'hillo'
import store from '@/model/global/store'

const userPrimaryStorePath = 'userPrimaryStore'
const userPinCode = 'userPinCode'
const userPinCodeStatus = 'userPinCodeStatus'

export async function login () {
  await determinedActivationStep()
}

export async function getActiveProductCode (deviceId = '') {
  let realDeviceId = ''
  realDeviceId = deviceId || await getCurrentDeviceId()
  return await getActiveCodes(realDeviceId)
}

export async function determinedActivationStep () {
  console.time('saasTimer')
  if (requireOfflineMode()) {
    const deviceId = await getCurrentDeviceId(getOfflineUrl() + '/PHP/')
    await useStore(deviceId, false)
    await gotoLastRoute()
  } else {
    if (router.currentRoute.name === 'cancel' || router.currentRoute.name === 'success') {
      markAppInitialized()
      return
    }
    if (Remember.shouldWait5Seconds) {
      await sleep(5 * 1000)
      Remember.shouldWait5Seconds = false
    }

    const mainStoreDevice = (await getUserCurrentStoreInfo())
    if (mainStoreDevice) {
      GlobalConfig.DeviceId = mainStoreDevice.currentStoreId
      try {
        const allDeviceSubscription = await getSubscriptionByCloudV2WithDeviceId(mainStoreDevice.currentStoreId) ?? []
        if (mainStoreDevice.currentStoreId > '6100' && mainStoreDevice.currentStoreId !== '9002') {
          const userStoreInfo = await getUserAndStoreRelationByDeviceId(mainStoreDevice.currentStoreId) ?? {}
          const baseProduct = allDeviceSubscription.find(it => it.productCode === 'base')
          if (!baseProduct && !userStoreInfo.admin) {
            markAppInitialized()
            await router.push('/needOpenDevice')
            return
          }
          store.commit('getAdminTest', { test: !!userStoreInfo.admin })
        }
      } catch (e) {
        // 云端接口临时不可用时仍然进入门店，避免整个后台没有任何数据
        console.error('check store subscription failed', e)
      }
      await enterStore(mainStoreDevice.currentStoreId)
      if (mainStoreDevice.notReady) {
        await router.push('/waiting-activate')
      } else {
        await gotoLastRoute()
      }
    } else {
      const alreadyAddedStore = await getAllStoreIdForUser()
      if (alreadyAddedStore.length > 0) {
        await saveUserCurrentStoreInfo(alreadyAddedStore[0])
        // 之前这里只进入门店但不跳转，刷新页面时当前页面会保持“没有数据”的状态，
        // 进入门店后需要重新导航一次，让页面用新的 hillo 配置重新加载数据
        await useStore(alreadyAddedStore[0], false)
        await gotoLastRoute()
      } else {
        markAppInitialized()
        await router.push('/SaaS')
      }
    }
  }

  console.timeEnd('saasTimer')
}

function getRouteNameFromLocation () {
  try {
    const hash = window.location.hash ? window.location.hash.slice(1) : '/'
    return router.match(hash || '/').name || ''
  } catch (e) {
    return ''
  }
}

export async function gotoLastRoute () {
  // 刷新时如果在路由守卫记录 lastRoute 之前初始化就完成了（比如 Firebase/Firestore 很快），
  // lastRoute 还是空的，这里从当前 URL 里取回刷新前的目标路由，避免总是回到 dashboard
  let name = lastRoute || getRouteNameFromLocation()
  if (!name || name === 'LoadingPage' || name === 'LoginPage' || name === 'error_page') {
    name = 'dashboard'
  }
  resetLastRoute()
  if (name === router.currentRoute.name) {
    await router.push({ name: 'LoadingPage' })
  }

  await router.push({ name })
}

export async function saveUserCurrentStoreInfo (storeId, baseUrl = '') {
  await setDoc(doc(FireBaseStore, userPrimaryStorePath, getCurrentUserId()), {
    currentStoreId: storeId,
    baseUrl
  })
}

export async function getUserCurrentStoreInfo () {
  try {
    return (await docContentOf(doc(FireBaseStore, userPrimaryStorePath, getCurrentUserId())))
  } catch (e) {
    console.log('Not Login', e)
    return ''
  }
}

export async function getCurrentStoreId () {
  return (await getUserCurrentStoreInfo())?.currentStoreId.toString() ?? null
}

export async function getPinCode () {
  return (await docContentOf(doc(FireBaseStore, userPinCode, getCurrentUserId())))
}

export async function usePinCode (pinCodeStatus) {
  return await setDoc(doc(FireBaseStore, userPinCodeStatus, getCurrentUserId()), { pinCodeStatus })
}
/**
 * @returns {string | undefined}
 */
export function getCurrentUserId () {
  return FireBaseAuth.currentUser?.uid
}

export async function getAllSubscriptionForCurrentStore () {
  const deviceId = await getCurrentStoreId()
  return await resultOf(query(collection(FireBaseStore, 'saas-store-subscription', deviceId.toString(), 'subscriptions')))
}

export async function getShopQR (shopId) {
  return await hillo.get('https://cloud-v2.aaden.io/report/shop/' + shopId, {}, { silent: true })
}

export async function getALLSubscriptionByCloudV2 () {
  return await hillo.get('https://cloud-v2.aaden.io/subscriptions/all', {})
}

export async function getSubscriptionByCloudV2WithDeviceId (deviceId) {
  return (await hillo.get('https://cloud-v2.aaden.io/subscriptions/by-device/' + deviceId, {})).filter(it => it.status === 'trialing' || it.status === 'active')
}
